/**
 * Design-time detection of fanout shapes that scale badly.
 *
 * The engine spawns one child workflow per item when an array lands on a
 * single-value socket. That is fine once. Two problems only show up at scale,
 * and neither is visible while wiring a graph:
 *
 *  1. NESTED FANOUT — a fanout boundary inside another fanout's branch. Child
 *     counts multiply rather than add, so two individually-reasonable widths
 *     become an unreasonable product. An Await (`fan_in`) between them ends the
 *     outer region, so the two run in SEQUENCE rather than nested and peak
 *     concurrency falls from N x M to max(N, M). Not a free win: the join emits
 *     one entry per child, so the second region then fans over cohorts rather
 *     than individual items.
 *
 *  2. FANOUT-INVARIANT STEPS — a step inside a fanout branch whose inputs never
 *     trace back, through data edges, to the fanned item. Every child runs it
 *     with identical inputs.
 *
 * An invariant step is not automatically a bug: the dependency may be real but
 * carried through the database rather than a socket. That is exactly why it is
 * worth surfacing — a dependency the engine cannot see is one it cannot scope
 * children against.
 *
 * Written against an incident (2026-08-06, Pennrose prod) where an email split
 * into 484 documents met an attachment-level fanout ~10 wide. The result was
 * ~4800 concurrent child executions, which filled Temporal's sticky-execution
 * cache and OOM-killed the shared workflow thread, taking every unrelated
 * workflow on the instance with it.
 *
 * ## What this cannot decide from a graph
 *
 * - `detectFanout` fans arrays of length > 1, and the length of an edge-fed
 *   array is a runtime value. Edge boundaries are POTENTIAL. Static-input
 *   widths are exact.
 * - The engine tests `Array.isArray` on the runtime value, not the declared
 *   socket type. A socket typed `json` that returns an array fans out
 *   invisibly; one typed `array<X>` returning a single item never fans.
 * - Steps pruned at runtime (an untaken Switch arm) are counted here, so branch
 *   and invariant counts are upper bounds.
 *
 * ## Where a branch ends
 *
 * Every walk here stops at an Await (`fan_in`), mirroring the engine's
 * `collectFannedRegion`: the Await and everything past it run ONCE in the
 * parent, not once per item. Walking through one would report two SEQUENTIAL
 * fanouts (widths add) as nested (widths multiply) — flagging the very fix that
 * prevents the blow-up above. An Await's own `value` input is likewise not a
 * boundary: `fan_in` is on `detectFanout`'s skip list, so nothing fans there.
 */

import {
  deriveWorkflowExpressionEdges,
  resolveActiveEdges,
} from "../expressions";
import { isArraySocketType, isFlowSocket } from "../types";
import { FAN_IN_STEP_TYPE } from "../utils/dynamicSockets/fanInOutputSockets";
import type { ValidationEdge, ValidationStep } from "./types";

/** Reserved config key holding pre-populated values for unwired input sockets. */
const STATIC_INPUTS_KEY = "__static_inputs";

type AnyEdge = Pick<
  ValidationEdge,
  "sourceStepId" | "sourceSocketId" | "targetStepId" | "targetSocketId"
>;

export interface FanoutBoundary {
  /** Null for a static-input fanout: the array is the step's own config. */
  sourceStepId: string | null;
  sourceSocketId: string | null;
  /** The step that runs once per item. */
  targetStepId: string;
  targetSocketId: string;
  origin: "edge" | "static_input";
  /** Item count, known only for static inputs. */
  knownWidth: number | null;
}

export interface FanoutFinding {
  boundary: FanoutBoundary;
  /** Steps that run once per item. */
  branchStepIds: string[];
  /** Branch steps whose inputs never trace back to the fanned item. */
  invariantStepIds: string[];
  /** Boundaries inside this branch whose widths multiply with this one's. */
  nestedBoundaryTargetIds: string[];
}

/**
 * True when an edge carries data rather than execution ordering.
 *
 * Mirrors `collectStepInputs`, which skips any edge whose TARGET socket is a
 * flow socket. Checking the source alone would treat a data output wired into a
 * flow-control input as a data dependency, which the engine discards.
 */
const isDataEdge = (edge: AnyEdge): boolean =>
  !isFlowSocket(edge.sourceSocketId) && !isFlowSocket(edge.targetSocketId);

function reachableFrom(
  startStepId: string,
  edges: AnyEdge[],
  {
    dataOnly = false,
    stopAt,
  }: { dataOnly?: boolean; stopAt?: ReadonlySet<string> } = {},
): Set<string> {
  const usable = dataOnly ? edges.filter(isDataEdge) : edges;

  const outgoing = new Map<string, string[]>();
  for (const edge of usable) {
    const targets = outgoing.get(edge.sourceStepId) ?? [];
    targets.push(edge.targetStepId);
    outgoing.set(edge.sourceStepId, targets);
  }

  const seen = new Set<string>();
  const queue = [startStepId];
  // Index cursor rather than shift(), so the loop needs no non-null assertion
  // and stays O(n) instead of re-indexing the array on every pop.
  for (let cursor = 0; cursor < queue.length; cursor++) {
    const current = queue[cursor];
    for (const next of outgoing.get(current) ?? []) {
      if (seen.has(next)) continue;
      // A stop node is neither reached nor traversed through — it, and
      // everything past it, belongs to the caller's "outside" set.
      if (stopAt?.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return seen;
}

/**
 * The Await (`fan_in`) steps in a graph — the boundaries a fanned region ends
 * at.
 *
 * `executeWorkflow` stops building a child's step list at one of these
 * (`collectFannedRegion`), so anything past an Await runs ONCE in the parent,
 * not once per item. Both questions this module answers — how wide a branch is,
 * and what multiplies with what — have to stop in the same place or they
 * describe a graph the engine does not run.
 */
function fanInStepIds(steps: ValidationStep[]): Set<string> {
  return new Set(
    steps
      .filter((step) => step.type === FAN_IN_STEP_TYPE)
      .map((step) => step.id),
  );
}

const socketTypeOf = (
  step: ValidationStep | undefined,
  socketId: string,
  side: "inputs" | "outputs",
): string | undefined =>
  step?.sockets[side].find((socket) => socket.id === socketId)?.type;

/**
 * Every place the engine will fan out.
 *
 * Two sources, because `collectStepInputs` merges both into the one `inputs`
 * record `detectFanout` reads:
 *
 *  1. An edge carrying an array output into a single-value input socket.
 *  2. `config.__static_inputs[socketId]` holding an array on a single-value
 *     socket with no incoming data edge. Static values seed UNWIRED sockets
 *     only, so a wired socket ignores its static default.
 */
export function findFanoutBoundaries(
  steps: ValidationStep[],
  edges: AnyEdge[],
  /**
   * Edges that decide whether a socket counts as "wired" for static-default
   * suppression. `collectStepInputs` uses the stored edges it was handed, so an
   * expression-only socket with no stored edge still takes its static default.
   * Defaults to `edges` when the caller does not distinguish them.
   */
  wiringEdges: AnyEdge[] = edges,
): FanoutBoundary[] {
  const byId = new Map(steps.map((step) => [step.id, step]));
  const boundaries: FanoutBoundary[] = [];

  // One boundary per fanned SOCKET, not per edge. `detectFanout` iterates
  // `Object.entries(inputs)` and fans a key once, so two array outputs wired
  // into the same scalar socket still produce a single fanout. Emitting one
  // boundary each would duplicate every downstream finding — the same sentence
  // twice in the node tooltip, and a double-counted boundary in the report.
  const claimedSockets = new Set<string>();

  const joins = fanInStepIds(steps);

  for (const edge of edges) {
    if (!isDataEdge(edge)) continue;

    // An array landing on an Await's `value` socket is NOT a fanout. `fan_in`
    // is the un-fanout and is on `detectFanout`'s skip list, so the engine
    // never fans it item-by-item — counting it as a boundary would invent a
    // region that does not exist, and (because a boundary's branch includes
    // its own target) would let an Await appear to enclose itself.
    if (joins.has(edge.targetStepId)) continue;

    const source = socketTypeOf(
      byId.get(edge.sourceStepId),
      edge.sourceSocketId,
      "outputs",
    );
    const target = socketTypeOf(
      byId.get(edge.targetStepId),
      edge.targetSocketId,
      "inputs",
    );

    if (isArraySocketType(source) && !isArraySocketType(target)) {
      const socketKey = `${edge.targetStepId}:${edge.targetSocketId}`;
      if (claimedSockets.has(socketKey)) continue;
      claimedSockets.add(socketKey);

      boundaries.push({
        sourceStepId: edge.sourceStepId,
        sourceSocketId: edge.sourceSocketId,
        targetStepId: edge.targetStepId,
        targetSocketId: edge.targetSocketId,
        origin: "edge",
        knownWidth: null,
      });
    }
  }

  const wiredSockets = new Set(
    wiringEdges
      .filter(isDataEdge)
      .map((edge) => `${edge.targetStepId}:${edge.targetSocketId}`),
  );

  for (const step of steps) {
    // Same exemption as the edge loop above: an Await is never fanned.
    if (joins.has(step.id)) continue;

    const staticInputs = step.config?.[STATIC_INPUTS_KEY];
    if (
      !staticInputs ||
      typeof staticInputs !== "object" ||
      Array.isArray(staticInputs)
    ) {
      continue;
    }

    for (const [socketId, value] of Object.entries(
      staticInputs as Record<string, unknown>,
    )) {
      if (isFlowSocket(socketId)) continue;
      if (wiredSockets.has(`${step.id}:${socketId}`)) continue;
      if (!Array.isArray(value) || value.length <= 1) continue;
      if (isArraySocketType(socketTypeOf(step, socketId, "inputs"))) continue;

      boundaries.push({
        sourceStepId: null,
        sourceSocketId: null,
        targetStepId: step.id,
        targetSocketId: socketId,
        origin: "static_input",
        knownWidth: value.length,
      });
    }
  }

  return boundaries;
}

/**
 * The steps that run once per fanned item.
 *
 * Mirrors the engine's `remainingStepIds` seeding exactly, including a subtlety
 * that is easy to get wrong: the seed is NOT the edge that supplied the array.
 * `executeWorkflow` looks the source up in the TRAVERSAL edges by target step
 * and target socket —
 *
 *     activeEdges.find(e => e.targetStepId === step.id
 *                        && e.targetSocketId === fanout.inputKey)
 *
 * — so when a socket is expression-backed but took its value from a stored
 * edge, the branch is still seeded from the EXPRESSION edge's source. Seeding
 * from the stored source instead walks a different subgraph and can miss
 * nested fanouts or invariant steps that really run in the child.
 *
 * From that source it seeds every downstream step, not just the fanout target,
 * so sibling branches converging on shared downstream steps land in the same
 * child. When no traversal edge feeds the socket — a static-input fanout, or an
 * expression-only socket — the engine falls back to seeding from the target.
 */
function branchFor(
  boundary: FanoutBoundary,
  traversalEdges: AnyEdge[],
  joins: ReadonlySet<string>,
): Set<string> {
  const traversalSource = traversalEdges.find(
    (edge) =>
      edge.targetStepId === boundary.targetStepId &&
      edge.targetSocketId === boundary.targetSocketId,
  )?.sourceStepId;

  const branch = reachableFrom(
    traversalSource ?? boundary.targetStepId,
    traversalEdges,
    { stopAt: joins },
  );
  branch.add(boundary.targetStepId);
  // The array source runs in the parent, not once per item.
  if (traversalSource !== undefined) branch.delete(traversalSource);
  return branch;
}

/**
 * Which edges feed a socket when STORED edges are primary.
 *
 * The `workflow-expression-resolution-enabled` flag defaults to off, and
 * `resolveStepInputsWithDualRead` then picks `edges` over `expressions`. So a
 * socket with a stored data edge takes its value from that edge and a competing
 * expression is inert; a socket with only an expression falls back to it.
 *
 * The mirror case — expressions primary, which is what the flag switches on —
 * is exactly `resolveActiveEdges`. Consumption is checked under BOTH, so the
 * analysis does not depend on how the flag is set. See `analyseFanoutTopology`.
 */
function storedPrimaryValueEdges(
  storedEdges: AnyEdge[],
  expressionEdges: AnyEdge[],
): AnyEdge[] {
  const storedTargets = new Set(
    storedEdges
      .filter(isDataEdge)
      .map((edge) => `${edge.targetStepId}:${edge.targetSocketId}`),
  );

  return [
    ...storedEdges,
    ...expressionEdges.filter(
      (edge) =>
        !storedTargets.has(`${edge.targetStepId}:${edge.targetSocketId}`),
    ),
  ];
}

/**
 * Analyse a graph's fanout topology. Pure.
 *
 * Three edge sets, because the questions genuinely differ:
 *
 *  - TRAVERSAL (`resolveActiveEdges`) — which steps are downstream. Mirrors
 *    `getExpressionDerivedEdges`, the set `executeWorkflow` walks to build
 *    `remainingStepIds`. An expression replaces the stored edge here.
 *
 *  - BOUNDARIES (the union) — which arrays could reach a socket at all. Which
 *    resolution wins is a runtime flag the graph does not encode, so a boundary
 *    is reported when EITHER path could produce one. Over-reporting a possible
 *    fanout costs a dismissed warning; missing one costs an outage.
 *
 *  - CONSUMPTION — whether a step actually receives the fanned item. Here the
 *    union is the WRONG direction: counting an inert edge as a dependency marks
 *    a step non-invariant and SUPPRESSES the warning. A step counts as
 *    consuming only if it consumes under BOTH resolutions — stored-primary
 *    (`storedPrimaryValueEdges`, the flag off) and expression-primary
 *    (`resolveActiveEdges`, the flag on). Equivalently: it is reported invariant
 *    if it is invariant under EITHER, so the answer does not depend on how
 *    `workflow-expression-resolution-enabled` happens to be set.
 *
 * All three lean the same way: toward saying something rather than staying
 * silent. The two consumption resolutions only diverge when a socket has both a
 * stored edge and an expression pointing somewhere else — the case the dual-read
 * divergence metric exists to catch, and itself worth a look.
 */
export function analyseFanoutTopology(
  steps: ValidationStep[],
  storedEdges: ValidationEdge[],
): FanoutFinding[] {
  const expressionSteps = steps.map((step) => ({
    id: step.id,
    inputSockets: step.sockets.inputs,
  }));

  const traversalEdges = resolveActiveEdges(expressionSteps, storedEdges);
  const joins = fanInStepIds(steps);
  const expressionEdges = deriveWorkflowExpressionEdges(expressionSteps);

  const seen = new Set<string>();
  const boundaryEdges: AnyEdge[] = [...storedEdges, ...expressionEdges].filter(
    (edge) => {
      const key = `${edge.sourceStepId}:${edge.sourceSocketId}->${edge.targetStepId}:${edge.targetSocketId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    },
  );

  // The two candidate value resolutions, one per setting of the flag.
  const storedPrimaryEdges = storedPrimaryValueEdges(
    storedEdges,
    expressionEdges,
  );

  // Static defaults seed UNWIRED sockets only, and `collectStepInputs` decides
  // "wired" from the stored edges it was handed — not the union.
  const boundaries = findFanoutBoundaries(steps, boundaryEdges, storedEdges);

  return boundaries.map((boundary) => {
    const branch = branchFor(boundary, traversalEdges, joins);

    // Consumes only if it consumes under BOTH resolutions — otherwise the step
    // loses its link to the fanned item whenever the flag is set the other way.
    const underStored = reachableFrom(
      boundary.targetStepId,
      storedPrimaryEdges,
      {
        dataOnly: true,
      },
    );
    const underExpressions = reachableFrom(
      boundary.targetStepId,
      traversalEdges,
      {
        dataOnly: true,
      },
    );
    const consumesFannedItem = new Set(
      [...underStored].filter((stepId) => underExpressions.has(stepId)),
    );
    consumesFannedItem.add(boundary.targetStepId);

    // Same-target boundaries are deliberately NOT nested. When one step has
    // several array inputs the engine either fans the first only
    // (`detectFanout`) or zips them into N aligned tuples on the batch path
    // (`detectLockstepFanout`, which throws on a length mismatch). Both yield N
    // children, never N x M — calling it nested would overstate the risk.
    const nestedBoundaryTargetIds = [
      ...new Set(
        boundaries
          .filter(
            (other) =>
              other.targetStepId !== boundary.targetStepId &&
              branch.has(other.targetStepId),
          )
          .map((other) => other.targetStepId),
      ),
    ];

    return {
      boundary,
      branchStepIds: [...branch],
      invariantStepIds: [...branch].filter(
        (stepId) => !consumesFannedItem.has(stepId),
      ),
      nestedBoundaryTargetIds,
    };
  });
}
